<template>
    <div class="file-viewer-xlsx tw:flex tw:flex-col tw:h-full tw:min-h-0" data-test="file-viewer-xlsx">
        <div
            v-if="loading"
            class="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:text-gray-500"
            data-test="file-viewer-xlsx-loading"
        >
            Loading spreadsheet…
        </div>

        <div
            v-else-if="error"
            class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-4"
            data-test="file-viewer-xlsx-error"
        >
            <p class="tw:text-gray-700">Could not render spreadsheet.</p>
        </div>

        <template v-else>
            <file-viewer-controls class="tw:flex-wrap tw:justify-end" data-test="file-viewer-xlsx-controls">
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-xlsx-zoom-out"
                    aria-label="Zoom out"
                    :disabled="zoom <= minZoom"
                    @click="zoomOut"
                >
                    <i class="uil uil-minus" aria-hidden="true" />
                </button>
                <span class="tw:text-sm tw:tabular-nums" data-test="file-viewer-xlsx-zoom" aria-live="polite">
                    {{ Math.round(zoom * 100) }}%
                </span>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-xlsx-zoom-in"
                    aria-label="Zoom in"
                    :disabled="zoom >= maxZoom"
                    @click="zoomIn"
                >
                    <i class="uil uil-plus" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-xlsx-fit-width"
                    aria-label="Fit to width"
                    @click="resetZoom"
                >
                    <i class="uil uil-expand-arrows-alt" aria-hidden="true" />
                </button>
            </file-viewer-controls>

            <div class="tw:flex-1 tw:min-h-0 tw:overflow-hidden" data-test="file-viewer-xlsx-table">
                <div ref="host" class="file-viewer-xlsx__host tw:h-full tw:w-full tw:min-h-0" />
            </div>
        </template>
    </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import FileViewerControls from '@/components/file-viewer/FileViewerControls.vue';

import { exceljsWorkbookToUniverData } from '@/utilities/exceljsToUniverWorkbook.js';

const props = defineProps({
    src: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['ready', 'error']);

const loading = ref(true);
const error = ref(false);
const host = ref(null);
const minZoom = 0.5;
const maxZoom = 3;
const zoomStep = 0.25;
const zoom = ref(1);

let loadId = 0;
/** @type {{ univer: { dispose: () => void }, univerAPI: any } | null} */
let univerRuntime = null;

function teardownUniver() {
    try {
        univerRuntime?.univer?.dispose?.();
    } catch {
        // Dispose best-effort; preview close should still succeed.
    }
    univerRuntime = null;
    if (host.value) {
        host.value.replaceChildren();
    }
}

/**
 * @param {number} nextZoom
 */
function applyZoom(nextZoom) {
    zoom.value = nextZoom;
    try {
        const sheet = univerRuntime?.univerAPI?.getActiveWorkbook?.()?.getActiveSheet?.();
        sheet?.setZoomRatio?.(nextZoom);
        sheet?.zoom?.(nextZoom);
    } catch {
        // Zoom API differences across Univer versions should not break preview.
    }
}

function zoomIn() {
    applyZoom(Math.min(maxZoom, Number((zoom.value + zoomStep).toFixed(2))));
}

function zoomOut() {
    applyZoom(Math.max(minZoom, Number((zoom.value - zoomStep).toFixed(2))));
}

function resetZoom() {
    applyZoom(1);
}

/**
 * @param {string} url
 */
async function loadXlsx(url) {
    const id = ++loadId;
    loading.value = true;
    error.value = false;
    zoom.value = 1;
    teardownUniver();

    try {
        const resp = await fetch(url, { credentials: 'same-origin' });
        if (id !== loadId) {
            return;
        }
        if (!resp.ok) {
            throw new Error(`Failed to fetch spreadsheet (${resp.status})`);
        }

        const arrayBuffer = await resp.arrayBuffer();
        if (id !== loadId) {
            return;
        }

        const ExcelJS = (await import('exceljs')).default;
        if (id !== loadId) {
            return;
        }

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        if (id !== loadId) {
            return;
        }

        const workbookData = exceljsWorkbookToUniverData(workbook);
        if (!workbookData.sheetOrder.length) {
            throw new Error('No worksheets found in spreadsheet');
        }

        loading.value = false;
        await nextTick();
        if (id !== loadId || !host.value) {
            return;
        }

        await import('@univerjs/preset-sheets-core/lib/index.css');
        const { createUniver, LocaleType, mergeLocales } = await import('@univerjs/presets');
        const { UniverSheetsCorePreset } = await import('@univerjs/preset-sheets-core');
        // eslint-disable-next-line import/extensions -- package locale subpath export
        const enUS = (await import('@univerjs/preset-sheets-core/locales/en-US')).default;

        if (id !== loadId || !host.value) {
            return;
        }

        const runtime = createUniver({
            locale: LocaleType.EN_US,
            locales: {
                [LocaleType.EN_US]: mergeLocales(enUS),
            },
            presets: [
                UniverSheetsCorePreset({
                    container: host.value,
                    header: false,
                    toolbar: false,
                    // Keep the sheet bar so multi-sheet workbooks remain navigable (AC-4).
                    footer: true,
                    formulaBar: false,
                    contextMenu: false,
                }),
            ],
        });

        if (id !== loadId) {
            runtime.univer.dispose();
            return;
        }

        univerRuntime = runtime;
        runtime.univerAPI.createWorkbook(workbookData);
        applyZoom(1);
        emit('ready');
    } catch (e) {
        if (id !== loadId) {
            return;
        }
        teardownUniver();
        error.value = true;
        loading.value = false;
        emit('error', e);
    } finally {
        if (id === loadId) {
            loading.value = false;
        }
    }
}

watch(
    () => props.src,
    value => {
        if (value) {
            loadXlsx(value);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    loadId += 1;
    teardownUniver();
});
</script>

<style scoped>
.file-viewer-xlsx__host {
    min-height: 0;
}
</style>
